/**
 * Export helpers for Konva floor-plan stages (no extra dependencies).
 * PDF = open a print window with the PNG (browser “Save as PDF”).
 */

export function exportStageAsPng(stage, { filename = 'floor-plan.png', pixelRatio = 2 } = {}) {
  if (!stage) return null;
  const uri = stage.toDataURL({ pixelRatio, mimeType: 'image/png' });
  const link = document.createElement('a');
  link.download = filename;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return uri;
}

export function exportStageAsPdf(stage, {
  filename = 'floor-plan',
  title = 'Floor plan',
  pixelRatio = 2,
} = {}) {
  if (!stage) return;
  const uri = stage.toDataURL({ pixelRatio, mimeType: 'image/png' });
  const win = window.open('', '_blank');
  if (!win) {
    // Popup blocked — fall back to PNG download
    exportStageAsPng(stage, { filename: `${filename}.png`, pixelRatio });
    return;
  }
  win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <style>
      @page { margin: 12mm; }
      html, body { margin: 0; padding: 0; background: #fff; }
      .wrap { display: flex; flex-direction: column; align-items: center; padding: 16px; font-family: system-ui, sans-serif; }
      h1 { font-size: 14px; font-weight: 500; color: #111; margin: 0 0 12px; }
      img { max-width: 100%; height: auto; }
      @media print { .hint { display: none; } }
      .hint { font-size: 12px; color: #888; margin-top: 12px; }
    </style></head><body>
    <div class="wrap">
      <h1>${title}</h1>
      <img src="${uri}" alt="${title}" />
      <p class="hint">Use Print → Save as PDF</p>
    </div>
    <script>
      const img = document.querySelector('img');
      img.onload = () => { setTimeout(() => window.print(), 250); };
    </script>
  </body></html>`);
  win.document.close();
}

/** Bounding box of shapes in canvas coordinates (with padding). */
export function getShapesBounds(shapes = [], padding = 40) {
  if (!shapes.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const s of shapes) {
    const x = s.x || 0;
    const y = s.y || 0;
    if (s.elementType === 'wall' || s.shapeType === 'line') {
      const pts = s.points?.length >= 4 ? s.points : [0, 0, s.width || 100, 0];
      minX = Math.min(minX, x + pts[0], x + pts[2]);
      minY = Math.min(minY, y + pts[1], y + pts[3]);
      maxX = Math.max(maxX, x + pts[0], x + pts[2]);
      maxY = Math.max(maxY, y + pts[1], y + pts[3]);
      continue;
    }
    if (s.elementType === 'door') {
      const r = Math.max(s.width || 56, 24);
      minX = Math.min(minX, x - 4);
      minY = Math.min(minY, y - r - 4);
      maxX = Math.max(maxX, x + r + 4);
      maxY = Math.max(maxY, y + 4);
      continue;
    }
    const w = s.width || 40;
    const h = s.height || 40;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }

  if (!Number.isFinite(minX)) return null;
  return {
    x: Math.max(0, minX - padding),
    y: Math.max(0, minY - padding),
    width: Math.max(100, maxX - minX + padding * 2),
    height: Math.max(100, maxY - minY + padding * 2),
  };
}
